import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useGlobalContext } from "../../ContextApi/GlobalContext";
import {
  getCompanyDetails, // Renamed for clarity to match  flow
  updateAdminProfilePicture,
  changeAdminPassword,
} from "../../Backend/profileFunction";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const Profile = () => {
  const { dispatch } = useGlobalContext();
  const fileInputRef = useRef(null);

  // --- STATE ---
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [passForm, setPassForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passLoading, setPassLoading] = useState(false);

  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // --- FETCH DATA ---
  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    setLoading(true);
    const result = await getCompanyDetails(); // Fetching admin-specific node data
    if (result.success) {
      setAdminData(result.data);
      if (dispatch) {
        dispatch({ companyData: result.data }); // Syncing with global state
      }
    } else {
      Swal.fire(
        "Error",
        result.message || "Failed to load admin profile",
        "error",
      );
    }
    setLoading(false);
  };

  // --- HELPERS ---
  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";
    const date = new Date(dateInput);
    return isNaN(date.getTime())
      ? "N/A"
      : new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(date);
  };

  // Helper for nested subscription data
  const getSubField = (field) => {
    if (!adminData?.subscription) return "N/A";
    return adminData.subscription[field] || "N/A";
  };

  // --- HANDLERS ---
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const result = await updateAdminProfilePicture(file);

    if (result.success) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Admin profile picture updated.",
        toast: true,
        position: "top-end",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchAdminProfile();
    } else {
      Swal.fire("Error", result.message, "error");
    }
    setUploading(false);
  };

  const handlePassSubmit = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) {
      return Swal.fire("Error", "Passwords do not match.", "error");
    }

    setPassLoading(true);
    const result = await changeAdminPassword(
      passForm.oldPassword,
      passForm.newPassword,
    );
    setPassLoading(false);

    if (result.success) {
      Swal.fire("Success", "Master access key updated.", "success");
      setPassForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } else {
      Swal.fire("Error", result.error || "Failed to update key.", "error");
    }
  };

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
        {/* HEADER */}
        <div className="mb-6 lg:mb-8 hidden lg:block">
          <h1 className="text-3xl font-black text-[#0B1C2D] tracking-tighter uppercase">
            Admin Identity
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your master credentials and organizational node status.
          </p>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center text-gray-400 gap-2">
            <span className="w-6 h-6 border-2 border-gray-300 border-t-[#E63946] rounded-full animate-spin"></span>
            Synchronizing Node...
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="xl:col-span-2 space-y-8">
              {/* 1. IDENTITY CARD */}
              <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#E63946]/5 rounded-bl-[120px] -z-0" />

                <div className="relative group shrink-0 z-10">
                  <div className="w-28 h-28 rounded-[2rem] border-[4px] border-white shadow-xl overflow-hidden bg-gray-50">
                    {adminData?.profilePicURL ? (
                      <img
                        src={adminData.profilePicURL}
                        alt="Admin"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#0B1C2D] text-white flex items-center justify-center text-4xl font-black uppercase">
                        {adminData?.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    className="absolute inset-0 bg-black/40 rounded-[2rem] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer backdrop-blur-[2px]"
                  >
                    <span className="material-symbols-outlined text-white text-[28px]">
                      upload_file
                    </span>
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                </div>

                <div className="text-center sm:text-left flex-1 z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2 justify-center sm:justify-start">
                    <h2 className="text-2xl font-black text-[#0B1C2D] tracking-tight uppercase">
                      {adminData?.name}
                    </h2>
                    <span
                      className={`px-3 py-1 text-[9px] font-black rounded-full uppercase tracking-[0.2em] border ${adminData?.status === true ? "bg-green-50 text-green-700 border-green-100" : "bg-orange-50 text-orange-700 border-orange-100"}`}
                    >
                      {adminData?.status === true
                        ? "Verified Access"
                        : "Pending Approval"}
                    </span>
                  </div>
                  <p className="text-[#64748B] text-sm font-bold mb-4 flex items-center justify-center sm:justify-start gap-2">
                    <span className="material-symbols-outlined text-[16px]">
                      verified_user
                    </span>
                    {adminData?.email}
                  </p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl">
                    <span className="material-symbols-outlined text-[#E63946] text-[18px]">
                      terminal
                    </span>
                    <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">
                      {adminData?.role || "Chief Administrator"}
                    </span>
                  </div>
                </div>
              </div>

              {/* 2. ADMINISTRATIVE CONTEXT GRID (Modified to match sendData fields) */}
              <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
                <h3 className="text-sm font-black text-[#0B1C2D] mb-8 flex items-center gap-2 uppercase tracking-widest">
                  <span className="material-symbols-outlined text-[#E63946]">
                    hub
                  </span>
                  Organizational Context
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <InputGroup
                    label="Organization"
                    value={adminData?.companyName}
                    readOnly
                  />
                  <InputGroup
                    label="Master Node URL"
                    value={adminData?.websiteUrl}
                    readOnly
                  />
                  <InputGroup
                    label="Emergency Contact"
                    value={adminData?.mobile}
                    readOnly
                  />
                  <InputGroup
                    label="Provisioned On"
                    value={formatDate(adminData?.joinedDate)}
                    readOnly
                  />
                  <InputGroup
                    label="Last Login Access"
                    value={formatDate(adminData?.lastLoginAt)}
                    readOnly
                  />
                  <InputGroup
                    label="Administrative ID"
                    value={adminData?.adminId}
                    readOnly
                  />
                </div>
              </div>

            </div>

            {/* --- RIGHT COLUMN: MASTER KEY SECURITY --- */}
            <div className="xl:col-span-1">
              <div className="bg-[#0B1C2D] p-8 rounded-[32px] shadow-2xl text-white sticky top-10">
                <h3 className="text-sm font-black mb-2 flex items-center gap-2 uppercase tracking-widest">
                  <span className="material-symbols-outlined text-[#E63946]">
                    key
                  </span>
                  Security HQ
                </h3>
                <p className="text-xs text-slate-400 mb-8 font-medium">
                  Rotate your master access key regularly to maintain
                  infrastructure integrity.
                </p>
                <form onSubmit={handlePassSubmit} className="space-y-6">
                  <SecurityInput
                    label="Current Master Key"
                    name="oldPassword"
                    value={passForm.oldPassword}
                    show={showOldPass}
                    setShow={setShowOldPass}
                    onChange={(e) =>
                      setPassForm({ ...passForm, oldPassword: e.target.value })
                    }
                  />
                  <SecurityInput
                    label="New Master Key"
                    name="newPassword"
                    value={passForm.newPassword}
                    show={showNewPass}
                    setShow={setShowNewPass}
                    onChange={(e) =>
                      setPassForm({ ...passForm, newPassword: e.target.value })
                    }
                  />
                  <SecurityInput
                    label="Confirm New Key"
                    name="confirmPassword"
                    value={passForm.confirmPassword}
                    show={showConfirmPass}
                    setShow={setShowConfirmPass}
                    onChange={(e) =>
                      setPassForm({
                        ...passForm,
                        confirmPassword: e.target.value,
                      })
                    }
                  />
                  <button
                    type="submit"
                    disabled={passLoading}
                    className="w-full py-5 bg-[#E63946] text-white font-black rounded-2xl shadow-xl shadow-red-900/40 hover:scale-[1.02] transition-all disabled:opacity-50"
                  >
                    {passLoading ? "PROCESSING..." : "UPDATE ACCESS KEY"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---
const InputGroup = ({ label, value, readOnly }) => (
  <div className="space-y-2">
    <label className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2">
      {label}
    </label>
    <div
      className={`w-full px-5 py-4 rounded-2xl border text-sm font-bold transition-all ${readOnly ? "bg-slate-50 border-slate-100 text-slate-500" : "bg-white border-slate-200 text-[#0B1C2D]"}`}
    >
      {value || "NOT PROVISIONED"}
    </div>
  </div>
);

const SecurityInput = ({ label, name, value, show, setShow, onChange }) => (
  <div className="space-y-2">
    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">
      {label}
    </label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-[#E63946] outline-none transition-all pr-12 text-sm font-bold"
        placeholder="••••••••"
        required
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
      >
        <span className="material-symbols-outlined text-[18px]">
          {show ? "visibility" : "visibility_off"}
        </span>
      </button>
    </div>
  </div>
);

export default Profile;
